import React, { useMemo } from 'react';
import { LineChart, TrendingUp } from 'lucide-react';

const ResponseTimeChart = ({ rawMetrics, timeRange }) => {
  const chartData = useMemo(() => {
    if (!rawMetrics || rawMetrics?.length === 0) return null;

    // Group metrics by time buckets
    const buckets = {};
    const bucketSize = timeRange === '1h' ? 5 * 60 * 1000 : // 5 minutes
                       timeRange === '24h' ? 60 * 60 * 1000 : // 1 hour
                       timeRange === '7d' ? 6 * 60 * 60 * 1000 : // 6 hours
                       24 * 60 * 60 * 1000; // 1 day

    rawMetrics?.forEach(metric => {
      const timestamp = new Date(metric.created_at)?.getTime();
      const bucketKey = Math.floor(timestamp / bucketSize) * bucketSize;

      if (!buckets?.[bucketKey]) {
        buckets[bucketKey] = {
          timestamp: bucketKey,
          responseTimes: [],
          openai: [],
          perplexity: []
        };
      }

      buckets?.[bucketKey]?.responseTimes?.push(metric?.response_time_ms);
      
      if (metric?.provider === 'openai') {
        buckets?.[bucketKey]?.openai?.push(metric?.response_time_ms);
      } else if (metric?.provider === 'perplexity') {
        buckets?.[bucketKey]?.perplexity?.push(metric?.response_time_ms);
      }
    });

    // Calculate averages
    const data = Object.values(buckets)?.sort((a, b) => a?.timestamp - b?.timestamp)?.map(bucket => ({
        timestamp: bucket?.timestamp,
        avg: Math.round(bucket?.responseTimes?.reduce((a, b) => a + b, 0) / bucket?.responseTimes?.length),
        openaiAvg: bucket?.openai?.length > 0 ? Math.round(bucket?.openai?.reduce((a, b) => a + b, 0) / bucket?.openai?.length) : null,
        perplexityAvg: bucket?.perplexity?.length > 0 ? Math.round(bucket?.perplexity?.reduce((a, b) => a + b, 0) / bucket?.perplexity?.length) : null
      }));

    const maxValue = Math.max(...data?.map(d => d?.avg));
    
    return { data, maxValue };
  }, [rawMetrics, timeRange]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    if (timeRange === '1h') {
      return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (timeRange === '24h') {
      return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <LineChart className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Response Time Trends</h3>
          <p className="text-sm text-gray-500">Average latency over time</p>
        </div>
      </div>
      {chartData && chartData?.data?.length > 0 ? (
        <>
          {/* Chart */}
          <div className="relative h-64 mb-4">
            <div className="absolute inset-0 flex items-end justify-between gap-2">
              {chartData?.data?.map((point, index) => {
                const height = (point?.avg / chartData?.maxValue) * 100;
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center group">
                    <div className="relative w-full">
                      {/* Bar */}
                      <div
                        className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t transition-all duration-300 hover:from-purple-700 hover:to-purple-500"
                        style={{ height: `${height}%`, minHeight: '4px' }}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap">
                            <div className="font-semibold mb-1">{formatTime(point?.timestamp)}</div>
                            <div>Avg: {point?.avg}ms</div>
                            {point?.openaiAvg && <div className="text-blue-300">OpenAI: {point?.openaiAvg}ms</div>}
                            {point?.perplexityAvg && <div className="text-green-300">Perplexity: {point?.perplexityAvg}ms</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Time label (show every few points) */}
                    {index % Math.ceil(chartData?.data?.length / 6) === 0 && (
                      <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                        {formatTime(point?.timestamp)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-600 rounded"></div>
              <span className="text-gray-700">Average Response Time</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <TrendingUp className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No response time data available</p>
        </div>
      )}
    </div>
  );
};

export default ResponseTimeChart;